import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, Bank, Gavel, CheckCircle, ArrowsClockwise, Briefcase } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { todayKey } from '@/lib/diaryStore';
import { fetchOperationsDiary, type DiaryItem } from '@/lib/operationsApi';
import { updateHearing } from '@/lib/hearingsApi';

/**
 * Court Board — court-centric operational view ("what's happening at each court today?").
 * Presentation only: groups the existing /operations/diary by Court → Courtroom → Judge →
 * Session → Time. No new endpoint, no computed readiness/obligations/lifecycle (display canonical
 * values). Actions use existing flows.
 */
export default function CourtBoardPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [day, setDay] = useState<string>(() => todayKey());

  const from = `${day}T00:00:00.000Z`;
  const to = `${day}T23:59:59.999Z`;

  const q = useQuery({
    queryKey: ['court-board', day],
    queryFn: () => fetchOperationsDiary({ scope: 'supported', from, to }),
    staleTime: 60_000,
  });

  const confirm = useMutation({
    mutationFn: (hearingId: string) => updateHearing(hearingId, { status: 'confirmed' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['court-board', day] }); toast.success('Attendance confirmed'); },
    onError: () => toast.error('Could not confirm — try the hearing page'),
  });

  // Group hearings: Court → Courtroom (with judge) → time-sorted rows.
  const courts = useMemo(() => groupByCourt(q.data ?? []), [q.data]);
  const shiftDay = (n: number) => {
    const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); setDay(d.toISOString().slice(0, 10));
  };

  return (
    <div className="space-y-5 px-1 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Bank className="h-6 w-6 text-brand-600 dark:text-brand-400" weight="duotone" />
          <div>
            <h1 className="text-xl font-bold tracking-tight text-foreground">Court Board</h1>
            <p className="text-xs text-muted-foreground">What&apos;s happening at each court</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => shiftDay(-1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-muted"><CaretLeft className="h-4 w-4" /></button>
          <button onClick={() => setDay(todayKey())} className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">Today</button>
          <button onClick={() => shiftDay(1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-muted"><CaretRight className="h-4 w-4" /></button>
          <span className="ml-2 text-sm font-medium text-foreground">{new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
        </div>
      </div>

      {q.isLoading && <div className="text-sm text-muted-foreground">Loading court listings…</div>}
      {q.isError && <div className="text-sm text-danger">Couldn&apos;t load court listings. <button className="underline" onClick={() => q.refetch()}>Retry</button></div>}
      {!q.isLoading && !q.isError && courts.length === 0 && (
        <Card><CardContent className="flex flex-col items-center gap-2 py-14 text-center">
          <Gavel className="h-9 w-9 text-muted-foreground/30" weight="duotone" />
          <div className="text-sm font-semibold text-foreground">No court listings</div>
          <p className="text-xs text-muted-foreground">No hearings for your barristers on {new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}.</p>
        </CardContent></Card>
      )}

      {courts.map((court) => (
        <Card key={court.name}>
          <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-4 py-2.5">
            <Bank className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            <span className="text-sm font-bold text-foreground">{court.name}</span>
            <Badge variant="outline">{court.count}</Badge>
          </div>
          <CardContent className="space-y-3 p-3">
            {court.rooms.map((room) => (
              <div key={room.key}>
                <div className="mb-1 flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <span>{room.courtroom}</span>
                  {room.judge && <span className="font-normal normal-case text-muted-foreground/80">· {room.judge}</span>}
                </div>
                <div className="rounded-lg border border-border/50">
                  {room.items.map((it) => (
                    <div key={it.id} className="flex flex-wrap items-center gap-2 border-b border-border/40 px-3 py-2 last:border-0 hover:bg-muted/20">
                      <span className="w-11 shrink-0 text-sm font-semibold tabular-nums text-foreground">{it.startAt.slice(11, 16)}</span>
                      <button onClick={() => it.hearing && navigate(`/clerk/hearings/${it.hearing.id}`)} className="min-w-0 flex-1 text-left">
                        <div className="truncate text-sm text-foreground">{it.matter ? `${it.matter.number} · ${it.matter.name}` : it.title}</div>
                        <div className="truncate text-[11px] capitalize text-muted-foreground">
                          {[it.hearing?.type, it.owner?.name].filter(Boolean).join(' · ')}
                        </div>
                      </button>
                      <div className="flex shrink-0 items-center gap-1">
                        {it.readiness && it.readiness.percentage < 100 && (
                          <Badge variant={it.readiness.percentage < 50 ? 'danger' : 'warning'}>{it.readiness.percentage}%</Badge>
                        )}
                        {it.obligations && it.obligations.blocked > 0 && <Badge variant="danger">{it.obligations.blocked} blocked</Badge>}
                        {it.hearing?.status === 'confirmed'
                          ? <Badge variant="success">Confirmed</Badge>
                          : it.hearing && (
                            <button
                              onClick={() => it.hearing && confirm.mutate(it.hearing.id)}
                              disabled={confirm.isPending}
                              className="inline-flex items-center gap-1 rounded-md border border-emerald-200 dark:border-emerald-500/30 px-2 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:bg-emerald-500/10 disabled:opacity-50"
                              title="Confirm attendance"
                            ><CheckCircle className="h-3.5 w-3.5" /> Confirm</button>
                          )}
                        {it.matter && (
                          <button onClick={() => navigate(`/clerk/matters/${it.matter!.id}`)} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-muted" title="Open matter">
                            <Briefcase className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {it.matter && (
                          <button onClick={() => navigate(`/clerk/matters/${it.matter!.id}`)} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-muted" title="Reassign counsel">
                            <ArrowsClockwise className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

interface Room { key: string; courtroom: string; judge: string | null; items: DiaryItem[] }
interface CourtGroup { name: string; count: number; rooms: Room[] }

function groupByCourt(items: DiaryItem[]): CourtGroup[] {
  const hearings = items.filter((i) => !!i.hearing).sort((a, b) => a.startAt.localeCompare(b.startAt));
  const courts = new Map<string, Map<string, Room>>();
  for (const it of hearings) {
    const courtName = it.matter?.court ?? 'Court to be confirmed';
    const roomName = it.hearing?.courtroom ?? 'Courtroom TBC';
    const roomKey = `${roomName}`;
    const rooms = courts.get(courtName) ?? new Map<string, Room>();
    const room = rooms.get(roomKey) ?? { key: roomKey, courtroom: roomName, judge: it.hearing?.judge ?? null, items: [] };
    if (!room.judge && it.hearing?.judge) room.judge = it.hearing.judge;
    room.items.push(it);
    rooms.set(roomKey, room);
    courts.set(courtName, rooms);
  }
  return [...courts.entries()].map(([name, rooms]) => {
    const roomList = [...rooms.values()].sort((a, b) => a.courtroom.localeCompare(b.courtroom));
    return { name, rooms: roomList, count: roomList.reduce((n, r) => n + r.items.length, 0) };
  }).sort((a, b) => a.name.localeCompare(b.name));
}
